import React, { useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import CurrencyInput from 'react-currency-input-field';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';
import { Map } from 'immutable';
import moment from 'moment';

import { upperCaseWords } from '../../utils/string';
import { noop } from '../../utils/noop';
import uniqueId from '../../utils/uniqueId';
import triggerInputChange from '../../utils/triggerInputChange';
import DeepValue, { PureDeepValue } from '../decorators/DeepValue';
import ChangeDetection, { PureChangeDetection } from '../decorators/ChangeDetection';
import DisplayMap from '../decorators/DisplayMap';
import NullAsEmptyString from '../decorators/NullAsEmptyString';
import EmailVerificationInput from './EmailVerificationInput';

/**
 * Inline input with a hidden label.
 * If you need to show label - use `showLabel={true}` property.
 *
 * @param props {Object} You can provide any other available on <input/>.
 * @param [props.id = uniqueId()] {string}.
 * @param props.name {string}.
 * @param props.placeholder {string}.
 * @param [props.defaultValue] {any}.
 * @param [props.value] {any} Can be instance of Immutable.Map. Then try to find originalValue and value keys in it.
 * @param [props.originalValue=null] {string|null} Is `null` will not check current value with originalValue.
 *    Else if value !== currentValue mark input by red triangle.
 * @param [props.label] {string|React.Component}.
 * @param [props.type=`'text'`] {string} Input's type attr.
 * @param [props.className=`''`] {string} Extra input`s class names. Need not to add `"form-control"`.
 * @param [props.showLabel=`false`] {boolean}.
 */
const PureInlineInputComponent = memo(props => {
  const {
    id: propsId,
    label,
    name,
    placeholder = '',
    type = 'text',
    showLabel = false,
    changed = false,
    value,
    defaultValue,
    originalValue,
    inputRef,
    canDisplayError,
    phoneType,
    onBlur,
    onChange = noop,
    ...extraProps
  } = props;

  const internalId = useRef(uniqueId());
  const id = propsId || internalId.current;

  const values = useMemo(() => getValues(props), [props]);
  // A plain <input> with an explicit empty value (e.g. an unset ChangeSpy field) must stay controlled,
  // otherwise React warns when the value is filled in later. Inputs driven by defaultValue are left as is.
  const controlledValues = useMemo(
    () => ('value' in values && !('defaultValue' in values) && values.value == null ? { value: '' } : values),
    [values],
  );
  const labelClassName = useMemo(() => classNames({ hidden: !showLabel }), [showLabel]);
  const className = useMemo(() => classNames('form-control', props.className, { changed }), [props.className, changed]);

  const handleBlur = useCallback(
    (event, ...rest) => {
      if (!phoneType && name !== 'phone') {
        if (onBlur) onBlur(event, ...rest);

        return;
      }

      const currentValue = value || '';

      if (currentValue[0] === '+') {
        if (onBlur) onBlur(event, ...rest);

        return;
      }

      const digitsOnly = String(currentValue).replace(/[^\d]/g, '');

      const formatAndBlur = digits => {
        const input = document.getElementById(id);

        input.value = `${digits[0]}${digits[1]}${digits[2]}-${digits[3]}${digits[4]}${digits[5]}-${digits[6]}${digits[7]}${digits[8]}${digits[9]}`;
        setImmediate(() => {
          triggerInputChange(input);
          if (onBlur) onBlur(event, ...rest);
        });
      };

      if (digitsOnly.length === 10) {
        setImmediate(() => formatAndBlur(digitsOnly));
      } else if (digitsOnly.length === 11 && String(digitsOnly[0]) === '1') {
        setImmediate(() => formatAndBlur(digitsOnly.substring(1)));
      } else if (onBlur) onBlur(event, ...rest);
    },
    [phoneType, name, value, id, onBlur],
  );

  if (type === 'money') {
    return (
      <MoneyInput
        {...extraProps}
        className={className}
        id={id}
        label={label}
        name={name}
        onChange={handleBlur}
        placeholder={placeholder}
        type="text"
        values={values}
      />
    );
  }

  if (type === 'date') {
    return (
      <DateInput
        {...props}
        autoComplete="off"
        className={className}
        labelClassName={labelClassName}
        onBlur={handleBlur}
        placeholder={placeholder}
        popperContainer={({ children }) => createPortal(children, document.body)}
        popperModifiers={{
          preventOverflow: { enabled: true, escapeWithReference: false, boundariesElement: 'viewport' },
          hide: { enabled: false },
        }}
        value={moment(values.value).isValid() ? moment(values.value) : moment(defaultValue)}
      />
    );
  }

  if (type === 'currency') {
    return (
      <PureCurrencyInput
        {...extraProps}
        className={className}
        id={id}
        label={label}
        labelClassName={labelClassName}
        name={name}
        onBlur={handleBlur}
        onChange={onChange}
        placeholder={placeholder}
        type="text"
        values={values}
      />
    );
  }

  if (type === 'web') {
    return (
      <PureWebInput
        {...extraProps}
        className={className}
        id={id}
        label={label}
        labelClassName={labelClassName}
        name={name}
        onBlur={handleBlur}
        onChange={onChange}
        placeholder={placeholder}
        type="text"
        values={values}
      />
    );
  }

  if (type === 'emailVerify') {
    return (
      <EmailVerificationInput
        {...extraProps}
        className={className}
        id={id}
        label={label}
        name={name}
        onBlur={handleBlur}
        onChange={onChange}
        placeholder={placeholder}
        showLabel={showLabel}
        type="email"
        values={values}
      />
    );
  }

  return (
    <span className="control-wrap">
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>
      <span className="control-wrap">
        <input
          {...extraProps}
          {...controlledValues}
          ref={inputRef}
          className={className}
          id={id}
          name={name}
          onBlur={handleBlur}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
        />
      </span>
    </span>
  );
});

const PureInlineInput = PureDeepValue(PureChangeDetection(NullAsEmptyString(DisplayMap(PureInlineInputComponent))));

const InlineInput = DeepValue(ChangeDetection(NullAsEmptyString(DisplayMap(props => <PureInlineInput {...props} />))));

InlineInput.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.any,
  id: PropTypes.string,
  label: PropTypes.any,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  originalValue: PropTypes.string,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.any,
};

const PureWebInput = memo(props => {
  const { id, label, labelClassName, values, canDisplayError, inputRef, ...extraProps } = props;
  const inputElementRef = useRef(null);

  // Keep latest props in a ref so handleBlur can read onChange/name/onBlur
  // without stripping them from `extraProps`. If we destructured `onChange`
  // out of props, it would be missing on the <input/>, making it read-only
  // (controlled value without onChange) and breaking keyboard input.
  const propsRef = useRef(props);

  useEffect(() => {
    propsRef.current = props;
  });

  const setRef = useCallback(
    input => {
      inputElementRef.current = input;
      if (inputRef) {
        inputRef(input);
      }
    },
    [inputRef],
  );

  const handleBlur = useCallback(event => {
    const { value = '' } = event.target;
    const { onChange, name, onBlur } = propsRef.current;

    if (value) {
      let url = value.replace(/^https?:\/\//gi, '');

      if (url.indexOf('/') > 0) {
        url = url.substring(0, url.indexOf('/'));
      }

      if (onChange) {
        onChange({
          target: {
            name,
            value: url,
          },
        });
      }
    }

    if (onBlur) {
      onBlur(event);
    }
  }, []);

  return (
    <span className="control-wrap">
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>
      <span className="control-wrap">
        <input {...extraProps} {...values} ref={setRef} onBlur={handleBlur} />
      </span>
    </span>
  );
});

const PureCurrencyInput = ({ id, label, labelClassName, values, canDisplayError, ...extraProps }) => {
  // Avoid mutating props — create derived value instead
  const formattedValues = useMemo(() => {
    if (!values) return values;

    return {
      ...values,
      value: values.value ? `$${values.value}` : values.value,
    };
  }, [values]);

  return (
    <span className="control-wrap">
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>

      <span className="control-wrap">
        <input id={id} {...extraProps} {...formattedValues} />
      </span>
    </span>
  );
};

/**
 * Inline input with hidden label.
 * If you need to show label - use `showLabel={true}` property.
 *
 * @param props {Object} You can provide any other available on <input/>.
 * @param [props.id = uniqueId()] {string}.
 * @param props.name {string}.
 * @param props.placeholder {string}.
 * @param [props.defaultValue] {any}.
 * @param [props.originalValue=null] {string|null} Is `null` will not check current value with originalValue.
 *    Else if value !== currentValue mark input by red triangle.
 * @param [props.label] {string|React.Component}.
 * @param [props.type=`'text'`] {string} Input's type attr.
 * @param [props.className=`''`] {string} Extra input`s class names. Need not to add `"form-control"`.
 * @param [props.showLabel=`false`] {boolean}.
 */
const DateInput = ({
  id: propsId,
  label,
  name,
  placeholder = '',
  showLabel = false,
  changed = false,
  onChange,
  value,
  autoFocus,
  dateFormat,
  className: propsClassName,
  ...extraProps
}) => {
  const internalId = useRef(uniqueId());

  const id = propsId || internalId.current;

  // Auto focus logic (replacement for componentDidUpdate)
  useEffect(() => {
    if (autoFocus) {
      const timer = setTimeout(() => {
        try {
          document.getElementById(id)?.focus();
        } catch (error) {
          console.error(error);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [autoFocus, id]);

  const selected = useMemo(
    () => (value && value.isValid && value.isValid() ? { selected: value } : { selected: null }),
    [value],
  );

  const labelClassName = useMemo(() => classNames({ hidden: !showLabel }), [showLabel]);

  const className = useMemo(
    () =>
      classNames('form-control', propsClassName, {
        changed,
      }),
    [propsClassName, changed],
  );

  const handleChange = selectedDate => {
    onChange({
      target: {
        name,
        value: moment(selectedDate, dateFormat),
      },
    });
  };

  return (
    <div>
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>

      <div className="control-wrap">
        <DatePicker
          {...extraProps}
          {...selected}
          className={className}
          dateFormat="MM/DD/YYYY"
          dropdownMode="select"
          id={id}
          name={name}
          onChange={selectedDate => handleChange(selectedDate)}
          placeholderText={upperCaseWords(placeholder)}
          popperPlacement="top-end"
          showMonthDropdown
          showYearDropdown
        />
      </div>
    </div>
  );
};

DateInput.propTypes = {
  className: PropTypes.string,
  defaultValue: PropTypes.any,
  id: PropTypes.string,
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  originalValue: PropTypes.string,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.any,
};

const MoneyInput = ({ id, label, labelClassName, values, canDisplayError, precision = 0, onChange, ...extraProps }) => {
  const value = useMemo(() => {
    if (!values?.value) return 0;

    if (typeof values.value === 'string') {
      return Number(values.value.replace(/,/g, '')) || 0;
    }

    return values.value;
  }, [values]);

  return (
    <span className="control-wrap">
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>

      <span className="control-wrap">
        <CurrencyInput
          id={id}
          {...extraProps}
          allowDecimals={precision > 0}
          allowNegativeValue={false}
          decimalsLimit={precision}
          onValueChange={onChange}
          value={value}
        />
      </span>
    </span>
  );
};

/**
 * Input row with hidden label.
 * If you need to show label - use `showLabel={true}` property.
 *
 * @param props {Object}.
 * @param props.id {string}.
 * @param props.name {string}.
 * @param props.placeholder {string}.
 * @param [props.defaultValue] {any} ,.
 * @param [props.label] {string|React.Component}.
 * @param [props.type=`'text'`] {string} Input's type attr.
 * @param [props.className=`''`] {string} Extra input`s class names. Need not to add `"form-control"`.
 * @param [props.showLabel=`false`] {boolean}.
 */
const PureInput = props => <PureInlineInput {...props} />;

const Input = props => <InlineInput {...props} />;

Input.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.any,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  type: PropTypes.string,
};

export { InlineInput, PureInlineInput, Input, PureInput };
export default PureInput;

function getValues(props) {
  const result = {};

  if ('defaultValue' in props) {
    result.defaultValue = props.defaultValue;
  }

  if ('value' in props) {
    result.value = props.value;
  }

  if (Map.isMap(result.value)) {
    result.value = result.value.get('value');
  }

  return result;
}
